import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Mail, MapPin, ShieldCheck, ChevronRight } from "lucide-react";
import { SITE } from "../mock";

const SECTIONS = [
  {
    n: "1",
    title: "Information We Collect",
    intro: "When you fill out a lead form on our website, we may collect the following information:",
    list: [
      "Name",
      "Email address",
      "Phone number",
      "Any specific messages or requirements you provide",
    ],
  },
  {
    n: "2",
    title: "How We Use Your Data",
    intro: "We use the information we collect to:",
    list: [
      "Respond to your direct enquiries.",
      "Provide the products, services, or information you requested, including detailed information about the neighbourhood area.",
      "Send relevant marketing communications (only if you have opted in).",
      "Improve our website experience and customer service.",
    ],
  },
  {
    n: "3",
    title: "Data Storage and Security",
    intro: "We take data security seriously. Your information is handled through secure measures:",
    listKV: [
      ["Storage", "Data is stored on secure, encrypted servers and trusted Customer Relationship Management (CRM) systems."],
      ["Retention", "We only keep your data for as long as necessary to fulfill the purposes outlined in this policy or to comply with legal obligations."],
      ["Protection", "We use standard SSL encryption to protect your data during transmission."],
    ],
  },
  {
    n: "4",
    title: "Sharing Your Data",
    paragraph:
      "We do not sell, rent, or trade your personal information to third parties. We may only share your data with trusted third-party service providers who assist us in operating our website and conducting our business, provided those parties agree to keep this information strictly confidential.",
  },
  {
    n: "5",
    title: "Your Rights",
    intro: "Depending on your location, you have the following rights regarding your data:",
    list: [
      "The right to access the personal data we hold about you.",
      "The right to request that we correct any inaccurate information.",
      "The right to request the deletion of your data from our systems.",
      "The right to opt out of future marketing communications at any time via the unsubscribe link.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <Helmet>
        <title>Privacy Policy | Bhima Kamble — Godrej Elaris Magarpatta</title>
        <meta name="description" content="Privacy Policy for Bhima Kamble, authorized channel partner for Godrej Elaris Magarpatta, Pune. Learn how we collect, store and protect your personal information." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://newlaunchmagarpatta.online/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | Bhima Kamble — Godrej Elaris Magarpatta" />
        <meta property="og:description" content="How we collect, store and protect your data at Bhima Kamble — authorized channel partner for Godrej Elaris." />
        <meta property="og:url" content="https://newlaunchmagarpatta.online/privacy-policy" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Top bar */}
      <div className="bg-[#2a1212] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:text-[#C9A24B] transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to {SITE.projectName}</span>
          </Link>
          <a href={`tel:${SITE.phoneRaw}`} className="text-xs md:text-sm text-[#C9A24B] hover:text-white">
            {SITE.phone}
          </a>
        </div>
      </div>

      {/* Hero header */}
      <header className="bg-[#8B1E1E] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C9A24B] text-[#1a1a1a] mb-4">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl font-bold mb-3">Privacy Policy</h1>
          <span className="gold-divider" />
          <p className="text-[#f3e7cb] max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Bhima Kamble
          </p>

          {/* Breadcrumb */}
          <nav className="mt-6 flex items-center justify-center gap-1.5 text-xs text-white/70">
            <Link to="/" className="hover:text-[#C9A24B]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">Privacy Policy</span>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="bg-white border border-[#ecdfc9] rounded-lg shadow-sm p-6 md:p-10">
          <p className="text-gray-700 leading-relaxed mb-8 text-[15px]">
            We at <b className="text-[#8B1E1E]">Bhima Kamble</b> value your privacy. This
            Privacy Policy explains how we collect, store, use, and protect your personal information
            when you submit your data through our enquiry and lead forms.
          </p>

          {SECTIONS.map((s) => (
            <section key={s.n} className="mb-9 last:mb-0">
              <h2 className="font-serif-display text-xl md:text-2xl font-bold text-[#8B1E1E] mb-3 flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#faf1dc] text-[#8B1E1E] text-sm font-bold shrink-0 border border-[#ecdfc9]">
                  {s.n}
                </span>
                <span className="pt-0.5">{s.title}</span>
              </h2>
              {s.intro && <p className="text-gray-700 leading-relaxed mb-3 text-[14.5px] pl-11">{s.intro}</p>}
              {s.paragraph && <p className="text-gray-700 leading-relaxed text-[14.5px] pl-11">{s.paragraph}</p>}
              {s.list && (
                <ul className="space-y-2 pl-11">
                  {s.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-[14.5px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9A24B] mt-2 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.listKV && (
                <ul className="space-y-3 pl-11">
                  {s.listKV.map(([k, v], i) => (
                    <li key={i} className="text-gray-700 text-[14.5px]">
                      <b className="text-[#5a1212]">{k}:</b> {v}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Contact Us — section 6 */}
          <section>
            <h2 className="font-serif-display text-xl md:text-2xl font-bold text-[#8B1E1E] mb-3 flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#faf1dc] text-[#8B1E1E] text-sm font-bold shrink-0 border border-[#ecdfc9]">
                6
              </span>
              <span className="pt-0.5">Contact Us</span>
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4 text-[14.5px] pl-11">
              If you have any questions about this Privacy Policy or how we handle your data, please contact us at:
            </p>
            <div className="pl-11 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#8B1E1E] text-white flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Email</div>
                  <a
                    href={`mailto:${SITE.email}`}
                    className="text-[15px] font-semibold text-[#8B1E1E] hover:underline break-all"
                  >
                    {SITE.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#8B1E1E] text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Address</div>
                  <div className="text-[15px] font-medium text-gray-800">
                    507, Global Business Hub, Kharadi, Pune - 411014
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="btn-primary">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </main>

      {/* Slim footer */}
      <footer className="border-t border-[#ecdfc9] bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-gray-500">
          <div>© {new Date().getFullYear()} Bhima Kamble. All rights reserved.</div>
          <div className="flex items-center gap-3">
            <span>MahaRERA: {SITE.agentRera}</span>
            <span className="text-gray-300">•</span>
            <Link to="/" className="hover:text-[#8B1E1E]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
